package com.fantasytenis.web.screens.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.bracket.entity.BracketChallengeEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketChallengeStatus
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen displaying list of available bracket challenges.
 */
@Composable
fun BracketChallengeListScreen() {
    val scope = rememberCoroutineScope()
    val showPaidGames by SharedData.showPaidGames.collectAsState()
    var challenges by remember { mutableStateOf<List<BracketChallengeEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedFilter by remember { mutableStateOf<BracketChallengeStatus?>(null) }

    // Load challenges on mount
    LaunchedEffect(selectedFilter, showPaidGames) {
        isLoading = true
        error = null
        val statusValue = selectedFilter?.let { status ->
            when (status) {
                BracketChallengeStatus.UPCOMING -> 1
                BracketChallengeStatus.LIVE -> 2
                BracketChallengeStatus.COMPLETED -> 3
                BracketChallengeStatus.CANCELLED -> 4
                BracketChallengeStatus.PAYING_OUT -> 5
            }
        }

        DependencyContainer.listBracketChallengesUseCase(
            status = statusValue,
            featuredOnly = false,
            limit = 50
        ).fold(
            onSuccess = { list ->
                // Filter based on platform setting
                challenges = if (showPaidGames) list else list.filter { it.entryFee == 0 }
                isLoading = false
            },
            onFailure = { e ->
                error = e.message
                isLoading = false
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = "Bracket Challenges",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.Home) }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            // Filter Tabs
            FilterTabs(
                selected = selectedFilter,
                onSelect = { selectedFilter = it }
            )

            // Content
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.listBracketChallengesUseCase().fold(
                            onSuccess = { challenges = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                challenges.isEmpty() -> EmptyState()
                else -> ChallengeGrid(challenges)
            }
            }
        }
    }
}

@Composable
private fun FilterTabs(
    selected: BracketChallengeStatus?,
    onSelect: (BracketChallengeStatus?) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(8.px)
            marginBottom(24.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        FilterTab("All", selected == null) { onSelect(null) }
        FilterTab("Upcoming", selected == BracketChallengeStatus.UPCOMING) {
            onSelect(BracketChallengeStatus.UPCOMING)
        }
        FilterTab("Live", selected == BracketChallengeStatus.LIVE) {
            onSelect(BracketChallengeStatus.LIVE)
        }
        FilterTab("Completed", selected == BracketChallengeStatus.COMPLETED) {
            onSelect(BracketChallengeStatus.COMPLETED)
        }
    }
}

@Composable
private fun FilterTab(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(8.px, 16.px)
            borderRadius(20.px)
            cursor("pointer")
            fontSize(14.px)
            fontWeight(if (isSelected) 600 else 400)
            backgroundColor(if (isSelected) Colors.Primary else Colors.Surface)
            color(if (isSelected) Colors.White else Colors.TextPrimary)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(
            text = "Retry",
            onClick = onRetry,
            variant = "primary"
        )
    }
}

@Composable
private fun EmptyState() {
    Div({
        style {
            textAlign("center")
            padding(64.px, 24.px)
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            margin(24.px, 0.px)
        }
    }) {
        // Icon placeholder
        Div({
            style {
                fontSize(48.px)
                marginBottom(16.px)
            }
        }) {
            Text("\uD83C\uDFC6") // Trophy emoji
        }

        H3({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(8.px)
            }
        }) {
            Text("No bracket challenges available")
        }

        P({
            style {
                color(Colors.TextSecondary)
                fontSize(14.px)
                marginBottom(24.px)
                lineHeight("1.5")
            }
        }) {
            Text("Bracket challenges become available during major tournaments. Check back during Grand Slams and ATP Masters events!")
        }

        Button(
            text = "Browse Contests",
            onClick = { NavigationState.navigate(Route.Contests) },
            variant = "primary"
        )
    }
}

@Composable
private fun ChallengeGrid(challenges: List<BracketChallengeEntity>) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
            gap(24.px)
        }
    }) {
        challenges.forEach { challenge ->
            ChallengeCard(challenge)
        }
    }
}

@Composable
private fun ChallengeCard(challenge: BracketChallengeEntity) {
    Div({
        classes("baseline-card")
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(20.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            cursor("pointer")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick {
            NavigationState.navigate(Route.BracketChallengeDetail(challenge.id))
        }
    }) {
        // Header with status badge
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            // Featured badge
            if (challenge.isFeatured) {
                Span({
                    style {
                        backgroundColor(Colors.Secondary)
                        color(Colors.TextPrimary)
                        fontSize(11.px)
                        fontWeight(600)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("FEATURED")
                }
            }

            // Status badge
            StatusBadge(challenge.status)
        }

        // Tournament name
        H3({
            style {
                h3Style()
                marginBottom(4.px)
                color(Colors.TextPrimary)
            }
        }) {
            Text(challenge.name)
        }

        // Tournament info
        P({
            style {
                color(Colors.TextSecondary)
                fontSize(13.px)
                marginBottom(16.px)
            }
        }) {
            Text(challenge.tournamentName)
        }

        // Entry info row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            InfoItem("Entry Fee", challenge.entryFeeDisplay)
            InfoItem("Prize Pool", challenge.prizePoolDisplay)
        }

        // Entries progress
        Div({
            style {
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    marginBottom(4.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Entries")
                }
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextPrimary)
                        fontWeight(500)
                    }
                }) {
                    val maxText = challenge.maxEntries?.toString() ?: "Unlimited"
                    Text("${challenge.currentEntries} / $maxText")
                }
            }

            // Progress bar
            if (challenge.maxEntries != null) {
                Div({
                    style {
                        width(100.percent)
                        height(4.px)
                        backgroundColor(Colors.Border)
                        borderRadius(2.px)
                        overflow("hidden")
                    }
                }) {
                    val fillPercent = (challenge.currentEntries.toDouble() / challenge.maxEntries!! * 100).coerceIn(0.0, 100.0)
                    Div({
                        style {
                            width(fillPercent.percent)
                            height(100.percent)
                            backgroundColor(Colors.Primary)
                            property("transition", "width 0.3s")
                        }
                    })
                }
            }
        }

        // Enter button
        if (!challenge.isLocked && !challenge.isFull) {
            Button(
                text = "Enter Challenge",
                onClick = {
                    NavigationState.navigate(Route.BracketChallengeDetail(challenge.id))
                },
                variant = "primary"
            )
        } else if (challenge.isLocked) {
            Button(
                text = "View Bracket",
                onClick = {
                    NavigationState.navigate(Route.BracketChallengeDetail(challenge.id))
                },
                variant = "secondary"
            )
        }
    }
}

@Composable
private fun StatusBadge(status: BracketChallengeStatus) {
    val (bgColor, textColor, label) = when (status) {
        BracketChallengeStatus.UPCOMING -> Triple(Colors.BracketStatus.UpcomingBackground, Colors.BracketStatus.UpcomingText, "Upcoming")
        BracketChallengeStatus.LIVE -> Triple(Colors.BracketStatus.LiveBackground, Colors.BracketStatus.LiveText, "Live")
        BracketChallengeStatus.COMPLETED -> Triple(Colors.BracketStatus.CompletedBackground, Colors.BracketStatus.CompletedText, "Completed")
        BracketChallengeStatus.CANCELLED -> Triple(Colors.BracketStatus.CancelledBackground, Colors.BracketStatus.CancelledText, "Cancelled")
        BracketChallengeStatus.PAYING_OUT -> Triple(Colors.BracketStatus.PayingOutBackground, Colors.BracketStatus.PayingOutText, "Paying Out")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun InfoItem(label: String, value: String) {
    Div {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(2.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}
